import { useEffect, useState } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { Clock, Globe, Shield, TrendingUp, Mail, Check } from 'lucide-react';
import apiService from '@/services/api';

const DEFAULTS = {
  // SLA & Response Windows
  jobAcceptanceWindow: 2,
  quotePrepSLA: 24,
  variationDecisionTarget: 4,
  qaReviewSLA: 8,
  globalEscalationThreshold: 48,

  // Global Defaults
  defaultTimezone: '(GMT+00:00) London',
  systemCurrency: 'GBP ($)',
  vatRate: 20.00,
  platformFee: 15.00,

  // Auto-Restriction Logic
  liabilityInsurance: true,
  tradeAccreditations: true,
  riskFlagPayout: true,
  complaintThreshold: 1.5,

  // Auto-Escalation Rules
  unreviewedVariations: 12,
  stalledDisputes: 3,
  urgentJobQueue: 5,
};

const PlatformRulesPage = () => {
  // (audit 2026-06-11) — was an entirely client-side form. Now persists
  // to /admin/platform-rules (system_config table) so Save/Reset actually
  // change platform behaviour.
  const [formData, setFormData] = useState(DEFAULTS);
  const [lastSaved, setLastSaved] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res: any = await apiService.request('/admin/platform-rules', 'GET');
        if (cancelled) return;
        if (res?.success && res.data?.rules) {
          setFormData({ ...DEFAULTS, ...res.data.rules });
          setLastSaved(res.data.updatedAt || null);
        }
      } catch (e) {
        console.error('Failed to load platform rules:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res: any = await apiService.request('/admin/platform-rules', 'PUT', formData);
      if (res?.success) {
        alert('Platform rules saved');
        setLastSaved(new Date().toISOString());
      } else {
        alert('Error: ' + (res?.message || 'Failed to save'));
      }
    } catch (e: any) {
      alert('Failed: ' + (e?.message || 'Network error'));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset all platform rules to factory defaults? Unsaved changes will be lost.')) return;
    setFormData(DEFAULTS);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : 
              name.includes('Rate') || name.includes('Fee') || name.includes('threshold') ? parseFloat(value) : 
              isNaN(parseFloat(value)) ? value : parseFloat(value)
    }));
  };

  const handleToggle = (field: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: !prev[field as keyof typeof formData]
    }));
  };

  const InputField = ({ label, sublabel, value, name, unit, onChange }: any) => (
    <div className="mb-6">
      <div className="flex gap-3 items-start">
        <div className="flex-1">
          <input
            type="number"
            name={name}
            value={value}
            onChange={onChange}
            className="w-16 px-3 py-2 border border-gray-300 rounded-lg font-semibold text-lg"
          />
          <p className="text-xs text-gray-500 font-medium mt-2">{label}</p>
          {sublabel && <p className="text-xs text-gray-400 mt-1">{sublabel}</p>}
        </div>
        {unit && <select className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium bg-white">
          <option>{unit}</option>
        </select>}
      </div>
    </div>
  );

  const ToggleField = ({ label, sublabel, checked, onChange }: any) => (
    <div className="flex items-start justify-between py-4 border-b border-gray-200 last:border-b-0">
      <div>
        <p className="font-semibold text-gray-900 text-sm">{label}</p>
        {sublabel && <p className="text-xs text-gray-600 mt-1">{sublabel}</p>}
      </div>
      <button
        onClick={onChange}
        className={`w-12 h-6 rounded-full transition-colors ${
          checked ? 'bg-teal-600' : 'bg-gray-300'
        }`}
      >
        <div className={`w-5 h-5 bg-white rounded-full transition-transform ${checked ? 'translate-x-6' : 'translate-x-0.5'}`} />
      </button>
    </div>
  );

  return (
    <AdminLayout>
      <div className="p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Platform Rules</h1>
          <p className="text-sm text-gray-600 max-w-2xl">
            Configure automated platform logic, SLA thresholds, and system defaults to maintain sovereign operational control.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 mb-8 items-center">
          {loading && <span className="text-xs text-gray-500 mr-auto">Loading…</span>}
          {lastSaved && !loading && (
            <span className="text-xs text-gray-500 mr-auto">Last saved: {new Date(lastSaved).toLocaleString()}</span>
          )}
          <button
            onClick={handleReset}
            disabled={loading || saving}
            className="px-6 py-2 border border-gray-300 hover:bg-gray-50 disabled:opacity-50 text-gray-900 font-semibold rounded-lg transition-colors"
          >
            Reset to Defaults
          </button>
          <button
            onClick={handleSave}
            disabled={loading || saving}
            className="px-6 py-2 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white font-semibold rounded-lg transition-colors"
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>

        {/* Grid Layout - Top Two Sections */}
        <div className="grid grid-cols-2 gap-6 mb-6">
          {/* SLA & Response Windows */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="w-5 h-5 text-blue-600" />
              <div>
                <h2 className="font-bold text-gray-900">SLA & Response Windows</h2>
                <p className="text-xs text-gray-500 font-medium">TEMPORAL GOVERNANCE</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <p className="text-xs uppercase font-bold text-gray-500 mb-2">Job Acceptance Window</p>
                <div className="flex gap-2">
                  <input type="number" value={formData.jobAcceptanceWindow} onChange={handleInputChange} name="jobAcceptanceWindow" className="w-16 px-3 py-2 border border-gray-300 rounded font-bold text-lg" />
                  <select className="px-3 py-2 border border-gray-300 rounded text-sm bg-white">
                    <option>Hours</option>
                  </select>
                </div>
                <p className="text-xs text-gray-500 mt-2">Maximum time for a provider to claim a job before it returns to pool.</p>
              </div>

              <div>
                <p className="text-xs uppercase font-bold text-gray-500 mb-2">Quote Preparation SLA</p>
                <div className="flex gap-2">
                  <input type="number" value={formData.quotePrepSLA} onChange={handleInputChange} name="quotePrepSLA" className="w-16 px-3 py-2 border border-gray-300 rounded font-bold text-lg" />
                  <select className="px-3 py-2 border border-gray-300 rounded text-sm bg-white">
                    <option>Hours</option>
                  </select>
                </div>
                <p className="text-xs text-gray-500 mt-2">Target duration for site visits to transition into formal quotes.</p>
              </div>

              <div>
                <p className="text-xs uppercase font-bold text-gray-500 mb-2">Variation Decision Target</p>
                <div className="flex gap-2">
                  <input type="number" value={formData.variationDecisionTarget} onChange={handleInputChange} name="variationDecisionTarget" className="w-16 px-3 py-2 border border-gray-300 rounded font-bold text-lg" />
                  <select className="px-3 py-2 border border-gray-300 rounded text-sm bg-white">
                    <option>Hours</option>
                  </select>
                </div>
                <p className="text-xs text-gray-500 mt-2">Allowed window for client approval of cost variations.</p>
              </div>

              <div>
                <p className="text-xs uppercase font-bold text-gray-500 mb-2">QA Review SLA</p>
                <div className="flex gap-2">
                  <input type="number" value={formData.qaReviewSLA} onChange={handleInputChange} name="qaReviewSLA" className="w-16 px-3 py-2 border border-gray-300 rounded font-bold text-lg" />
                  <select className="px-3 py-2 border border-gray-300 rounded text-sm bg-white">
                    <option>Hours</option>
                  </select>
                </div>
                <p className="text-xs text-gray-500 mt-2">Timeline for internal auditors to clear completion reports.</p>
              </div>

              <div>
                <p className="text-xs uppercase font-bold text-gray-500 mb-2">Global Escalation Threshold</p>
                <div className="flex gap-2">
                  <input type="number" value={formData.globalEscalationThreshold} onChange={handleInputChange} name="globalEscalationThreshold" className="w-16 px-3 py-2 border border-gray-300 rounded font-bold text-lg" />
                  <span className="px-3 py-2 text-sm font-bold text-teal-600">HOURS</span>
                </div>
              </div>
            </div>
          </div>

          {/* Global Defaults */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Globe className="w-5 h-5 text-purple-600" />
              <div>
                <h2 className="font-bold text-gray-900">Global Defaults</h2>
                <p className="text-xs text-gray-500 font-medium">SYSTEM ENVIRONMENT</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <p className="text-xs uppercase font-bold text-gray-500 mb-2">Default Timezone</p>
                <select className="w-full px-3 py-2 border border-gray-300 rounded text-sm bg-white">
                  <option>{formData.defaultTimezone}</option>
                </select>
              </div>

              <div>
                <p className="text-xs uppercase font-bold text-gray-500 mb-2">System Currency</p>
                <select className="w-full px-3 py-2 border border-gray-300 rounded text-sm bg-white">
                  <option>{formData.systemCurrency}</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs uppercase font-bold text-gray-500 mb-2">VAT Rate (%)</p>
                  <input type="number" step="0.01" value={formData.vatRate} onChange={handleInputChange} name="vatRate" className="w-full px-3 py-2 border border-gray-300 rounded font-bold" />
                </div>
                <div>
                  <p className="text-xs uppercase font-bold text-gray-500 mb-2">Platform Fee (%)</p>
                  <input type="number" step="0.01" value={formData.platformFee} onChange={handleInputChange} name="platformFee" className="w-full px-3 py-2 border border-gray-300 rounded font-bold text-teal-600" />
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs text-blue-900">
                <p className="font-medium">ℹ Changes to financial defaults will only apply to new contracts generated after the timestamp of saving.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Grid Layout - Bottom Two Sections */}
        <div className="grid grid-cols-2 gap-6 mb-6">
          {/* Auto-Restriction Logic */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Shield className="w-5 h-5 text-red-600" />
              <div>
                <h2 className="font-bold text-gray-900">Auto-Restriction Logic</h2>
                <p className="text-xs text-gray-500 font-medium">POLICY ENFORCEMENT</p>
              </div>
            </div>

            <div className="space-y-2">
              <ToggleField 
                label="Liability Insurance Expiry"
                sublabel="Automatically restrict providers if insurance documentation is past validity date."
                checked={formData.liabilityInsurance}
                onChange={() => handleToggle('liabilityInsurance')}
              />
              <ToggleField 
                label="Trade Accreditations"
                sublabel="Block new work assignment if mandatory trade certs are invalid."
                checked={formData.tradeAccreditations}
                onChange={() => handleToggle('tradeAccreditations')}
              />
              <ToggleField 
                label="Risk Flag Payout Hold"
                sublabel="Pause automated payouts for any account with an unresolved 'High risk' flag."
                checked={formData.riskFlagPayout}
                onChange={() => handleToggle('riskFlagPayout')}
              />
              
              <div className="pt-4 border-t border-gray-200">
                <p className="text-xs uppercase font-bold text-gray-500 mb-2">Complaint Threshold Suspension</p>
                <div className="flex gap-2 items-center">
                  <input type="number" step="0.1" value={formData.complaintThreshold} onChange={handleInputChange} name="complaintThreshold" className="w-16 px-3 py-2 border border-gray-300 rounded font-bold" />
                  <span className="text-sm font-medium text-gray-700">%</span>
                </div>
                <p className="text-xs text-gray-600 mt-2">Suspend if complaint rate over 30-day window exceeds limit.</p>
              </div>
            </div>
          </div>

          {/* Auto-Escalation Rules */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-5 h-5 text-green-600" />
              <div>
                <h2 className="font-bold text-gray-900">Auto-Escalation Rules</h2>
                <p className="text-xs text-gray-500 font-medium">EXECUTIVE OVERSIGHT</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-start justify-between p-3 border border-gray-200 rounded-lg">
                <div>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <p className="font-semibold text-gray-900 text-sm">Unreviewed Variations</p>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">Escalate to Senior Manager if unreviewed after:</p>
                </div>
                <div className="text-right">
                  <input type="number" value={formData.unreviewedVariations} onChange={handleInputChange} name="unreviewedVariations" className="w-12 px-2 py-1 border border-gray-300 rounded font-bold text-center" />
                  <p className="text-xs font-bold text-gray-700 mt-1">HRS</p>
                </div>
              </div>

              <div className="flex items-start justify-between p-3 border border-gray-200 rounded-lg">
                <div>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <p className="font-semibold text-gray-900 text-sm">Stalled Disputes</p>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">Escalate to Legal if no activity for:</p>
                </div>
                <div className="text-right">
                  <input type="number" value={formData.stalledDisputes} onChange={handleInputChange} name="stalledDisputes" className="w-12 px-2 py-1 border border-gray-300 rounded font-bold text-center" />
                  <p className="text-xs font-bold text-gray-700 mt-1">DAYS</p>
                </div>
              </div>

              <div className="flex items-start justify-between p-3 border border-gray-200 rounded-lg">
                <div>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <p className="font-semibold text-gray-900 text-sm">Urgent Job Queue</p>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">Notify Board if unassigned urgent jobs reach:</p>
                </div>
                <div className="text-right">
                  <input type="number" value={formData.urgentJobQueue} onChange={handleInputChange} name="urgentJobQueue" className="w-12 px-2 py-1 border border-gray-300 rounded font-bold text-center" />
                  <p className="text-xs font-bold text-gray-700 mt-1">COUNT</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Notification Triggers */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-2 mb-6">
            <Mail className="w-5 h-5 text-orange-600" />
            <div>
              <h2 className="font-bold text-gray-900">Notification Triggers</h2>
              <p className="text-xs text-gray-500 font-medium">PLATFORM COMMS CHANNEL</p>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-4">
            {[
              { name: 'Critical SLA Breach', desc: 'Triggers immediate Email and Push to account managers.' },
              { name: 'High-Value Quote', desc: 'Alert for any quote exceeding £1,000 submission threshold.' },
              { name: 'Repetitive Variations', desc: 'Flags providers requesting >3 variations on same job.' },
              { name: 'Health Summary', desc: 'Generate and send operational health report every Sunday 22:00.' }
            ].map((trigger, idx) => (
              <div key={idx} className="border border-gray-200 rounded-lg p-4 text-center">
                <button className="w-full flex justify-center mb-3">
                  <div className="w-8 h-8 rounded-lg bg-teal-100 flex items-center justify-center">
                    <Check className="w-5 h-5 text-teal-600" />
                  </div>
                </button>
                <p className="font-semibold text-gray-900 text-sm mb-1">{trigger.name}</p>
                <p className="text-xs text-gray-600">{trigger.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="grid grid-cols-3 gap-4 text-sm">
          <div>
            <p className="text-xs uppercase font-bold text-gray-500 mb-1">Last Rule Modification</p>
            <p className="font-semibold text-gray-900">
              {lastSaved ? new Date(lastSaved).toLocaleString() : 'Never saved'}
            </p>
          </div>
          <div>
            <p className="text-xs uppercase font-bold text-gray-500 mb-1">Active Automated Agents</p>
            {/* (audit 2026-06-12) — "14 Background Services" was a literal.
                Now derive a count from the actual auto-restriction +
                auto-escalation toggles the admin has enabled. */}
            <p className="font-semibold text-gray-900">
              {[
                formData.liabilityInsurance,
                formData.tradeAccreditations,
                formData.riskFlagPayout,
                Number(formData.unreviewedVariations) > 0,
                Number(formData.stalledDisputes) > 0,
                Number(formData.urgentJobQueue) > 0,
              ].filter(Boolean).length}{' '}
              Automated Rules Active
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${loading ? 'bg-yellow-500' : 'bg-green-500'}`}></div>
            <p className="text-xs uppercase font-bold text-gray-500">System Status</p>
            <p className="font-semibold text-gray-900">{loading ? 'LOADING' : saving ? 'SAVING' : 'OPERATIONAL'}</p>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default PlatformRulesPage;
